import { motion } from 'framer-motion';
import { MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useScrollAnimation, scrollAnimationVariants, transitionPresets } from '@/hooks/useScrollAnimation';

const SourcingMap = () => {
  const { ref, isInView } = useScrollAnimation({ margin: '-80px' as const });
  const { t } = useTranslation();

  const locations = [
    { key: 'scotland', flag: '🏴󠁧󠁢󠁳󠁣󠁴󠁿' },
    { key: 'norway', flag: '🇳🇴' },
    { key: 'sweden', flag: '🇸🇪' },
    { key: 'iceland', flag: '🇮🇸' },
    { key: 'france', flag: '🇫🇷' },
    { key: 'ireland', flag: '🇮🇪' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.4,
      },
    },
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 50,
      scale: 0.8,
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
    },
  };

  return (
    <section ref={ref} className="section-padding bg-card overflow-hidden relative z-10">
      <div className="container-premium">
        {/* Header with reveal animation */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <motion.span 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-accent font-medium tracking-wider uppercase text-sm inline-block"
          >
            {t('sourcing.label')}
          </motion.span>
          <motion.h2 
            initial={{ opacity: 0, y: 30, filter: 'blur(8px)' }}
            animate={isInView ? { opacity: 1, y: 0, filter: 'blur(0px)' } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mt-4 mb-6"
          >
            {t('sourcing.title')}
          </motion.h2>
          <motion.div 
            initial={{ scaleX: 0 }}
            animate={isInView ? { scaleX: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="section-divider mb-6 origin-center" 
          />
          <motion.p 
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-muted-foreground"
          >
            {t('sourcing.description')}
          </motion.p>
        </motion.div>

        {/* Map Visualization with 3D perspective */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, rotateX: 10 }}
          animate={isInView ? { opacity: 1, scale: 1, rotateX: 0 } : {}}
          transition={{ duration: 1, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="relative bg-primary/50 rounded-2xl p-8 md:p-12 overflow-hidden"
          style={{ transformStyle: 'preserve-3d', perspective: '1000px' }}
        >
          {/* Animated decorative waves */}
          <div className="absolute inset-0 opacity-10 overflow-hidden">
            <motion.svg 
              className="w-full h-full" 
              viewBox="0 0 100 100" 
              preserveAspectRatio="none"
            >
              <motion.path 
                fill="currentColor" 
                className="text-accent"
                initial={{ d: 'M0,50 Q25,45 50,50 T100,50 V100 H0 Z' }}
                animate={{ 
                  d: [
                    'M0,50 Q25,45 50,50 T100,50 V100 H0 Z',
                    'M0,50 Q25,55 50,50 T100,50 V100 H0 Z',
                    'M0,50 Q25,45 50,50 T100,50 V100 H0 Z',
                  ]
                }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              />
              <motion.path 
                fill="currentColor" 
                className="text-secondary"
                initial={{ d: 'M0,60 Q25,55 50,60 T100,60 V100 H0 Z' }}
                animate={{ 
                  d: [
                    'M0,60 Q25,55 50,60 T100,60 V100 H0 Z',
                    'M0,60 Q25,65 50,60 T100,60 V100 H0 Z',
                    'M0,60 Q25,55 50,60 T100,60 V100 H0 Z',
                  ]
                }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
              />
            </motion.svg>
          </div>

          {/* Locations Grid with staggered animations */}
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            className="relative grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6"
          >
            {locations.map((location, index) => (
              <motion.div
                key={location.key}
                variants={cardVariants}
                transition={{ 
                  duration: 0.6,
                  type: 'spring',
                  stiffness: 100,
                  damping: 12,
                }}
                whileHover={{ 
                  y: -8, 
                  scale: 1.05,
                  boxShadow: '0 20px 40px -15px rgba(0, 0, 0, 0.2)',
                }}
                className="bg-card/80 backdrop-blur-sm rounded-xl p-6 text-center border border-border hover:border-accent/50 transition-colors duration-300 cursor-pointer"
              >
                <motion.div 
                  className="text-4xl mb-3"
                  animate={{ 
                    rotate: [0, -5, 5, 0],
                    scale: [1, 1.1, 1],
                  }}
                  transition={{ 
                    duration: 2, 
                    repeat: Infinity, 
                    repeatDelay: 3 + index * 0.5,
                    ease: 'easeInOut'
                  }}
                >
                  {location.flag}
                </motion.div>
                <h3 className="font-serif text-lg font-semibold text-foreground mb-1">
                  {t(`sourcing.locations.${location.key}.name`)}
                </h3>
                <p className="text-sm text-muted-foreground">{t(`sourcing.locations.${location.key}.desc`)}</p>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={isInView ? { scale: 1 } : {}}
                  transition={{ 
                    delay: 0.8 + index * 0.1,
                    type: 'spring',
                    stiffness: 200,
                  }}
                >
                  <MapPin className="mx-auto mt-3 text-accent" size={18} />
                </motion.div>
              </motion.div>
            ))}
          </motion.div>

          {/* Animated connection lines */}
          <div className="absolute inset-0 pointer-events-none">
            <svg className="w-full h-full opacity-20" viewBox="0 0 100 100" preserveAspectRatio="none">
              <motion.line 
                x1="20" y1="50" x2="80" y2="50" 
                stroke="hsl(var(--accent))" 
                strokeWidth="0.2" 
                strokeDasharray="2,2"
                initial={{ pathLength: 0 }}
                animate={isInView ? { pathLength: 1 } : {}}
                transition={{ duration: 2, delay: 0.5 }}
              />
              <motion.line 
                x1="50" y1="20" x2="50" y2="80" 
                stroke="hsl(var(--accent))" 
                strokeWidth="0.2" 
                strokeDasharray="2,2"
                initial={{ pathLength: 0 }}
                animate={isInView ? { pathLength: 1 } : {}}
                transition={{ duration: 2, delay: 0.8 }}
              />
            </svg>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SourcingMap;
